<template>
  <v-app class="layout-wrapper">
    <loading :active.sync="busy"/>

    <!-- Error modal -->
    <modal-error></modal-error>

    <div class="content" v-if="is_received">

      <!-- Header -->
      <header-lab name="my_requests"></header-lab>

      <!-- No issues -->
      <div class="table-wrapper scrollbox">

        <section class="scrollbox-content"
          v-if="!tickets.length">
          <div class="empty-contents">
            <div class="empty-contents-title">
              {{ $t('issues.empty_title') }}
            </div>
            <div class="empty-contents-subtitle">
              {{ $t('issues.empty_subtitle') }}
            </div>
            <div class="empty-contents-button">
              <v-btn depressed @click="reportIssue()">
                {{ $t('issues.send_feedback') }}
              </v-btn>
            </div>
          </div>
        </section>


        <!-- List - table -->
        <section class="scrollbox-content" 
          v-if="!!tickets.length">

          <div class="list-header">
            <div class="content-search">
              <v-text-field
                v-model="search_keyword"
                transition="slide-x-reverse-transition"
                outlined
                prepend-inner-icon="mdi-magnify"
                hide-details
                clearable
                placeholder="Find issues...">
              </v-text-field>
            </div>
          </div>

          <div class="table">
            <div class="table-header">
              <div class="table-row">
                <div class="row-id">
                  {{ $t('issues.id') }}
                </div>
                <div class="row-type">
                  {{ $t('issues.type') }}
                </div>
                <div class="row-title">
                  {{ $t('issues.title') }}
                </div>
                <div class="row-created">
                  {{ $t('issues.created') }}
                </div>
                <div class="row-status">
                  {{ $t('issues.status') }}
                </div>
                <div class="row-details">
                  {{ $t('issues.detail') }}
                </div>
              </div>
            </div>

            <div class="table-contents"
              v-if="!!Object.keys(tk_types).length&&
              !!Object.keys(tk_status).length">
              <div class="table-row"
                v-for="(tk, i) in filtered_tickets" :key="i">
                <div class="row-id">
                  {{ tk.tk_id }}
                </div>
                <div class="row-type">
                  {{ $t('issues.' + tk_types[tk.type]) }}
                </div>
                <div class="row-title">
                  {{ tk.title }}
                </div>
                <div class="row-created">
                  {{ tk.created_ts.substr(0, 10) }}
                </div>
                <div class="row-status"
                  :class="{'ticket-reply':tk.status==2,
                    'ticket-answer':tk.status==3}">
                  {{ $t('issues.' + tk_status[tk.status]) }}
                  <v-tooltip bottom
                    v-if="tk.status==2||tk.status==3">
                    <template v-slot:activator="{ on }">
                      <v-icon small class="red--text" v-on="on">
                        mdi-alert-circle</v-icon>
                    </template>
                    <span v-if="tk.status==2">
                      {{ $t('issues.in_progress_info') }}
                    </span>
                    <span v-if="tk.status==3">
                      {{ $t('issues.solution_provided_info') }}
                    </span>
                  </v-tooltip>
                </div>
                <div class="row-details">
                  <v-btn small depressed
                    @click="selectTicket(tk.tk_id)">
                    {{ $t('issues.detail') }}
                  </v-btn>
                </div>
              </div>
            </div>

          </div>

        </section>

      </div>
    </div>

    <!-- Modal -->
    <modal-request-detail
      :dialog="request_detail_visible"
      :id="tk_id">
    </modal-request-detail>

    <modal-report-issue
      :dialog.sync="report_issue_visible">
    </modal-report-issue>

  </v-app>
</template>

<script>

import { mapGetters } from 'vuex'
import { ticket_status, ticket_types } from '../../../custom/model'

export default {
  metaInfo() {
    return {
      title: "Issues"
    }
  },

  data() {
    return {
      ticket_status: ticket_status,
      ticket_types: ticket_types,

      busy: false,
      request_detail_visible: false,
      report_issue_visible: false,

      filtered_tickets: [],
      search_keyword: '',
    }
  },

  computed: {
    ...mapGetters({
      tickets: 'ticket/my_tickets',
    }),

    is_received() {
      return Array.isArray(this.tickets)
    },

    tk_status() {
      let values = {}
      for (let i=0; i<this.ticket_status.length; i++) {
        values[this.ticket_status[i].id] = this.ticket_status[i].name
      }
      return values
    },

    tk_types() {
      let values = {}
      for (let i=0; i<this.ticket_types.length; i++) {
        values[this.ticket_types[i].id] = this.ticket_types[i].name
      }
      return values
    },

    tk_id() {
      return this.$route.params.id
    },
  },

  watch: {
    tickets: {
      handler(val) {
        this.search_keyword = ''
        this.updateTickets()
        this.checkTkId()
      },
      deep: true,
      immediate: true,
    },

    tk_id: {
      handler(val) {
        this.checkTkId()
      },
    },

    search_keyword: {
      handler(val) {
        if (!val || !val.length) {
          this.filtered_tickets = JSON.parse(JSON.stringify(this.tickets))
        } else {
          this.searchTickets(val)
        }
      },
    },
  },

  async created() {
    this.busy = true
    try {
      await this.$store.dispatch('ticket/getMyTickets')
    } catch(e) {
      this.$store.commit('error/setDialog', true)
      this.$store.commit('error/setErrorCode', e.response.status)
    } finally {
      this.busy = false
    }
  },

  methods: {
    updateTickets() {
      let new_tickets = []
      if (!!this.tickets) {
        new_tickets = JSON.parse(JSON.stringify(this.tickets))
      }
      this.filtered_tickets = new_tickets
    },

    checkTkId() {
      let vm = this
      if (!!this.tk_id) {
        let tk_ids = this.tickets.map(v => v.tk_id)
        if (tk_ids.includes(vm.tk_id)) {
          this.request_detail_visible = true
        } else {
          this.$router.replace({ name: 'issues' })
        }
      } else {
        this.request_detail_visible = false
      }
    },

    selectTicket(id) {
      this.$router.push({ name: 'issue', params: { id } })
      this.$analytics.logEvent('click', { name: 'issues_btn_select' })
    },

    searchTickets(keyword) {
      keyword = keyword.toLowerCase()
      this.filtered_tickets = this.tickets.filter(v => {
        return v.title.toLowerCase().includes(keyword)
      })
    },

    reportIssue() {
      this.report_issue_visible = true
      this.$analytics.logEvent('click', { name: 'issues_btn_report' })
    }
  },
}
</script>

<style scoped>

  .layout-wrapper {
    min-width: 1000px;
  }

  /* Header */

  .list-header {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 24px 24px 12px;
  }

  /* Empty */

  .empty-contents {
    display: flex;
    height: calc(100vh - 90px);
    padding-bottom: 100px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .empty-contents-title {
    font-size: 32px;
    padding-bottom: 16px;
  }

  .empty-contents-subtitle {
    font-size: 14px;
    padding-bottom: 7px;
  }

  .empty-contents-button {
    margin-top: 12px;
  }

  /* Table */

  .table-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: calc(100vh - 90px);
  }

  .table {
    padding: 0 40px 40px;
    display: flex;
    flex-direction: column;
  }

  .table-header {
    border-bottom: solid 1px #818387;
    color: #818387;
  }

  .table-row {
    display: grid;
    grid-template-columns: 10% 10% 45% 10% 15% 10%;
  }

  .table-row > div {
    padding: 8px;
    display: flex;
    align-items: center;
  }

  .row-id, .row-type, .row-created, .row-status {
    color: #818387;
  }

  .ticket-answer {
    color: #36749d;
  }

  .ticket-reply {
    color: #FF770D;
  }

  /* Commons */

  @media only screen and (min-width: 1264px) {
    .table {
      width: 1140px;
    }
  }

  @media (max-width: 1264px) {
    .table {
      width: 912px;
    }
  }

</style>


// WEBPACK FOOTER //
// src/components/spa/common/Issues.vue